/**
 * useCardTilt — mouse-tracking 3D tilt + shimmer for card surfaces.
 *
 * Sets CSS custom properties (--rotateX, --rotateY, --e1x/y, --e2x/y)
 * consumed by .rotate-dynamic and .dcm-shiny-overlay classes.
 *
 * Aliases: card-tilt, tilt-hook, mouse-tracking, perspective
 */
import { useCallback, useRef } from "react";

/** Gentler than button's 60 — large surfaces exaggerate tilt. */
const ROTATE_SCALE = 15;

export function useCardTilt<T extends HTMLElement>(): {
  readonly ref: React.RefObject<T | null>;
  readonly onMouseMove: (e: React.MouseEvent<T>) => void;
  readonly onMouseLeave: () => void;
} {
  const ref = useRef<T | null>(null);

  const onMouseMove = useCallback((e: React.MouseEvent<T>) => {
    const el = ref.current;
    if (!el) return;

    const rect = el.getBoundingClientRect();
    const xRatio = (e.clientX - rect.left) / rect.width;
    const yRatio = (e.clientY - rect.top) / rect.height;

    el.style.setProperty("--e1x", `${30 + xRatio * 60}%`);
    el.style.setProperty("--e1y", `${5 + yRatio * 40}%`);
    el.style.setProperty("--e2x", `${35 + xRatio * 20}%`);
    el.style.setProperty("--e2y", `${35 + yRatio * 20}%`);

    const rotateX =
      -(e.clientY - rect.y - rect.height / 2) / (rect.height / ROTATE_SCALE);
    const rotateY =
      (e.clientX - rect.x - rect.width / 2) / (rect.width / ROTATE_SCALE);

    el.style.setProperty("--rotateX", `${rotateX}deg`);
    el.style.setProperty("--rotateY", `${rotateY}deg`);
  }, []);

  const onMouseLeave = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    el.style.setProperty("--rotateX", "0deg");
    el.style.setProperty("--rotateY", "0deg");
    el.style.setProperty("--e1x", "45%");
    el.style.setProperty("--e1y", "45%");
    el.style.setProperty("--e2x", "45%");
    el.style.setProperty("--e2y", "45%");
  }, []);

  return { ref, onMouseMove, onMouseLeave } as const;
}
